<script setup lang="ts">
  import type { Recipe, RecipeTag } from '@/types/Recipe'
  import type { Document } from '@contentful/rich-text-types'
  import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
  import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

  const route = useRoute()
  const router = useRouter()
  const slug = computed(() => route.params.slug as string)
  const { data: recipe, pending } = useRecipe(slug.value)
  const { cart, addItem, removeItem, updateItem } = useCart()
  const { isPinned, togglePinned } = usePinned()
  const ingredientsRef = ref<HTMLElement>()

  function toText(content?: Recipe['intro']) {
    return content ? documentToPlainTextString(content as Document) : ''
  }

  function toHtml(content?: Recipe['intro']) {
    return content ? documentToHtmlString(content as Document) : ''
  }

  const recipeTitleString = computed(() => `${recipe.value?.title || ''} ${recipe.value?.subtitle || ''}`)
  const metaTags = computed(() => {
    const tags = [
      { property: 'og:title', content: `${recipeTitleString} | juss recipes` },
      { property: 'twitter:title', content: `${recipeTitleString} | juss recipes` },
    ]
    if (recipe.value?.intro) {
      const content = toText(recipe.value.intro)
      tags.push({ property: 'og:description', content }, { property: 'twitter:description', content })
    }
    if (recipe.value?.media?.length) {
      const content = recipe.value.media[0]
      tags.push({ property: 'og:image', content }, { property: 'twitter:image', content })
    }
    return tags
  })

  useHead({ title: recipeTitleString, meta: metaTags.value })

  function handleIngredientsClick(e: MouseEvent) {
    const target = e.target as HTMLElement
    const liEl = target?.closest('li')
    const checkboxEl = target?.closest('label.checkbox__wrapper')
    const removeButtonEl = target?.closest('.cart__itemRemove')
    const cartButtonEl = target?.closest('.cart__addedTag')
    if (!liEl) return

    const itemId = liEl.dataset.itemId
    const ingredient = liEl.querySelector('p')?.innerText ?? ''
    let item = cart.value.find(({ id, name }) => id === itemId || name.toLowerCase() === ingredient.toLowerCase())
    const found = Boolean(item)

    if (cartButtonEl) return router.push({ name: 'cart' })
    if (removeButtonEl && item) return removeItem(item)
    if (!found) item = addItem({ name: ingredient, recipeSlug: slug.value })
    if (item && (found || checkboxEl)) return updateItem({ ...item, recipeSlug: slug.value, complete: !item.complete })
  }

  watch(
    [cart],
    () => {
      if (!cart.value || !ingredientsRef.value) return

      // add checkbox element to list items if in cart
      const items = [...ingredientsRef.value.querySelectorAll('li')].filter((li) => !li.querySelector('ul'))
      const itemTexts = items.map((li) => li.querySelector('p')?.innerText.toLowerCase())
      const itemsInCart = cart.value.filter(({ name }) => itemTexts.includes(name.toLowerCase()))
      items.forEach((li) => {
        const cartItem = itemsInCart.find(
          ({ name }) => name.toLowerCase() === li.querySelector('p')?.innerText.toLowerCase(),
        )

        // add data id
        if (cartItem?.id) li.dataset.itemId = cartItem.id

        // add checkbox element
        let checkboxEl = li?.querySelector?.('.checkbox__wrapper')
        if (cartItem?.id) {
          if (!li.classList.contains('checkbox-enabled')) li.classList.add('checkbox-enabled')
          const checkboxHtml = `<label class="checkbox__wrapper"><input class="checkbox" type="checkbox"${
            cartItem?.complete ? ' checked="checked"' : ''
          }><span class="checkbox__animated"></span></label>`
          if (checkboxEl) {
            checkboxEl.outerHTML = checkboxHtml
          } else {
            li.innerHTML = `${checkboxHtml}${li.innerHTML}`
          }
        } else {
          if (li.classList.contains('checkbox-enabled')) li.classList.remove('checkbox-enabled')
          if (checkboxEl?.parentElement) checkboxEl.parentElement.removeChild(checkboxEl)
        }

        // add complete class
        if (cartItem?.complete) {
          if (!li.classList.contains('complete')) li.classList.add('complete')
        } else {
          li.classList.remove('complete')
        }

        // add remove button
        if (cartItem?.id) {
          const addedTag = '<i class="far fa-shopping-cart cart__addedTag"></i>'
          const rmButton = '<button class="cart__itemRemove"><i class="fal fa-times" aria-hidden="true"></i></button>'
          if (!li.querySelector('.cart__itemRemove')) li.innerHTML = `${li.innerHTML}${addedTag}${rmButton}`
        } else {
          // remove remove button
          if (li.querySelector('.cart__addedTag')) li.querySelector('.cart__addedTag')?.remove()
          if (li.querySelector('.cart__itemRemove')) li.querySelector('.cart__itemRemove')?.remove()
        }
      })
    },
    { deep: true },
  )
</script>

<template>
  <section class="page--recipe" :class="`recipe--${slug}`">
    <h1 class="recipe__title">{{ recipe?.title || slug?.replaceAll('-', ' ') || '' }}</h1>

    <Transition name="page" mode="out-in">
      <p v-if="pending" class="mt-4">Loading...</p>
      <div v-else>
        <h2 v-if="recipe?.subtitle" class="recipe__subtitle">{{ recipe?.subtitle }}</h2>

        <button
          v-if="recipe"
          class="group flex items-center px-3 py-1 mt-3 text-sm transition border-2 rounded-full"
          :class="
            isPinned(recipe)
              ? 'border-juss-purple-400 hover:border-juss-purple-300'
              : 'bg-white border-slate-200 hover:border-juss-purple-200'
          "
          @click.stop.prevent="togglePinned(recipe)"
        >
          <i
            class="fas fa-map-pin mr-1 transition"
            :class="
              isPinned(recipe)
                ? 'text-juss-purple-500 group-hover:text-juss-purple-500 group-hover:text-opacity-75'
                : 'text-slate-400 group-hover:text-juss-purple-400'
            "
          />
          <span
            class="transition"
            :class="
              isPinned(recipe)
                ? 'text-juss-purple-500 group-hover:text-juss-purple-500 group-hover:text-opacity-75 font-black'
                : 'text-slate-500 group-hover:text-slate-600 font-semibold'
            "
            >{{ isPinned(recipe) ? 'pinned' : 'pin' }}</span
          >
        </button>

        <!-- eslint-disable-next-line vue/no-v-html -->
        <section v-if="toText(recipe?.intro)" class="recipe__intro text--smol" v-html="toHtml(recipe?.intro)" />

        <section v-if="recipe?.prepTime || recipe?.totalTime || recipe?.serves" class="recipe__meta">
          <p v-if="recipe.prepTime" class="text--smol">
            <i class="fad fa-fw fa-gingerbread-man"></i>
            <span>Prep Time: {{ recipe.prepTime }}</span>
          </p>

          <p v-if="recipe.totalTime" class="text--smol">
            <i class="fad fa-fw fa-cauldron"></i>
            <span>Total Time: {{ recipe.totalTime }}</span>
          </p>

          <p v-if="recipe.serves" class="text--smol text--serves">
            <i class="fad fa-fw fa-utensils"></i>
            <span>Serves {{ recipe.serves }}</span>
          </p>
        </section>

        <section ref="ingredientsRef" class="recipe__ingredients" @click="handleIngredientsClick($event)">
          <h3>ingredients</h3>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="recipe__content recipe__content--ingredients" v-html="toHtml(recipe?.ingredients)" />
        </section>

        <section class="recipe__steps">
          <h3>steps</h3>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="recipe__content recipe__content--steps" v-html="toHtml(recipe?.steps)" />
        </section>

        <hr />

        <section v-if="recipe?.source" class="recipe__source text--smol">
          Recipe from/inspired by: {{ recipe.source }}
        </section>

        <section v-if="recipe?.tags?.length" class="recipe__tags">
          <NuxtLink
            v-for="tag in recipe.tags as RecipeTag[]"
            :key="tag"
            :to="{ path: '/', query: { search: tag } }"
            class="recipe__tag"
          >
            #{{ tag }}
          </NuxtLink>
        </section>

        <section v-if="recipe?.media?.length" class="recipe__media">
          <img v-for="media in recipe.media" :key="media" :src="media" :alt="recipe.title as string" />
        </section>
      </div>
    </Transition>
  </section>
</template>

<style lang="sass">
  .page--recipe
    .recipe__title
      font-size: 3em
      margin-bottom: 0
      line-height: 1.25
      @media (max-width: 480px)
        font-size: 2.25em

    .recipe__subtitle
      font-size: 1.8em
      color: rgba(0, 0, 0, 0.64)
      @media (max-width: 480px)
        font-size: 1.5em

    h3
      font-size: 1.8em
      color: #82788e
      @media (max-width: 480px)
        font-size: 1.5em

    .recipe__intro
      padding: 1em 1.2em
      border: 2px solid #d6d0de

    :is(.recipe__content, .recipe__intro)
      :is(h1, h2, h3, h4, h5, h6)
        font-size: 1.2em
        line-height: 1.25
        font-family: "Mansalva", serif
        font-weight: 900
        color: theme('colors.slate.600')
      :is(h4, h5, h6)
        font-size: 1em
      p
        margin: 1em 0
        &:first-of-type
          margin-top: 0
        &:last-of-type
          margin-bottom: 0
      ul, ol
        list-style: none
        padding: 0
        margin: 0
        li
          position: relative
          margin: 0
          padding-left: 1.5em
          margin-left: .2em
          margin-bottom: .5em
          &::before
            content: '\F00D'
            position: absolute
            top: 0
            left: 0
            height: 2.75em
            display: flex
            align-items: center
            font-size: .6em
            color: #b4a5c3

      ul li
        &::before
          font-family: 'Font Awesome 6 Pro'

      ol
        counter-reset: li
        li
          counter-increment: li
          &::before
            content: counter(li)
            height: unset
            align-items: flex-start
            font-size: .8em
            font-weight: 600
            margin-top: .2em

    section
      margin: 1.5em 0

    .text--smol
      font-size: 0.85em

    .recipe__meta .text--smol
      margin: .4em 0

    .text--smol.text--serves
      margin-top: 1em

    .recipe__meta i
      margin-right: 0.5em
      color: theme('colors.juss.purple.200')

    hr
      width: 100px
      margin: 1.5em 0
      border: none
      border-top: 1px solid #d6d0de

    .recipe__content li p
      margin: 0
      margin-bottom: 0.2em

    .recipe__source
      font-style: italic
      margin-top: .8em

    .recipe__tags .recipe__tag
      display: inline-block
      font-size: 0.8em
      font-weight: 500
      background: #7d6896
      color: white
      padding: 0.2em 0.5em
      border-radius: 10em
      text-decoration: none
      margin-right: 0.5em

    .recipe__media
      column-count: 2
      column-gap: 1em
      @media (max-width: 800px)
        column-count: 1
      img
        width: 100%
        margin: 0
        padding-bottom: 1em
        -webkit-column-break-inside: avoid
        page-break-inside: avoid
        break-inside: avoid-column
        display: table

    .recipe__ingredients
      li.checkbox-enabled:not(:has(ul))
        width: fit-content
        min-width: 300px
        display: flex
        align-items: center
        gap: 0.575em
        cursor: pointer
        margin-left: 0
        padding-left: 0
        &::before
          content: ''
        .checkbox__wrapper
          height: 2.25em
          align-self: flex-start
        @media (max-width: 600px)
          width: 100%
        &.complete
          opacity: 0.5
          text-decoration: line-through
        .cart__itemRemove
          color: theme('colors.slate.400')
          transition: color .2s ease-in-out
          &:hover
            color: theme('colors.slate.600')
        .cart__addedTag
          margin-left: auto
          color: theme('colors.slate.600')
          background-color: theme('colors.slate.200')
          font-size: 0.75rem
          padding: 0.25rem
          border-radius: 4px
          transition: background-color .2s ease-in-out
          &:hover
            background-color: theme('colors.slate.300')
</style>
